import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

// Import expert interview screens
import { ExpertWelcomeScreen } from '@/components/expert-interview/ExpertWelcomeScreen';
import { ExpertBriefingScreen } from '@/components/expert-interview/ExpertBriefingScreen';
import { ExpertSetupScreen } from '@/components/expert-interview/ExpertSetupScreen';
import { ExpertReadyScreen } from '@/components/expert-interview/ExpertReadyScreen';
import { ExpertRecordingScreen } from '@/components/expert-interview/ExpertRecordingScreen';
import { ExpertCompletionScreen } from '@/components/expert-interview/ExpertCompletionScreen';

type InterviewScreen =
  | 'welcome'
  | 'briefing'
  | 'setup'
  | 'ready'
  | 'recording'
  | 'processing'
  | 'complete';

interface InterviewData {
  id: string;
  expertName: string;
  expertTitle: string;
  expertCompany: string;
  expertBio?: string;
  expertEmail?: string;
  journalist: {
    name: string;
    title: string;
    bio?: string;
    photoUrl?: string;
    voiceGender: string;
  };
  approach: string;
  approachData: any;
  questions: Array<{
    id: string;
    text: string;
    type: string;
    priority?: string;
  }>;
  briefing: string;
  status: string;
  siteDomain: string;
  siteName?: string;
  language?: string;
  setupMode?: 'wizard' | 'instant' | 'self';
  isSelfInterview?: boolean;
  originalPrompt?: string;
}

export default function ExpertInterview() {
  const { interviewId } = useParams<{ interviewId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interview, setInterview] = useState<InterviewData | null>(null);
  const [currentScreen, setCurrentScreen] = useState<InterviewScreen>('welcome');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');

  // Debug: Log when selectedAudioDevice changes
  useEffect(() => {
    console.log('[ExpertInterview] 🎯 selectedAudioDevice state changed to:', selectedAudioDevice);
  }, [selectedAudioDevice]);

  useEffect(() => {
    if (!token) {
      setError('No access token provided. Please use the link from your email.');
      setLoading(false);
      return;
    }

    fetchInterview();
  }, [token]);

  // Poll for interview status changes when on processing screen
  useEffect(() => {
    if (currentScreen !== 'processing' || !token) {
      return;
    }

    const pollInterviewStatus = async () => {
      try {
        const response = await fetch(`/api/expert/interview?token=${encodeURIComponent(token)}`);
        if (response.ok) {
          const data = await response.json();

          // Auto-advance to complete screen when interview is marked complete
          if (data.interview.status === 'completed') {
            setCurrentScreen('complete');
            setInterview(data.interview);
          }
        }
      } catch (error) {
        console.error('Error polling interview status:', error);
      }
    };

    // Poll every 10 seconds
    const interval = setInterval(pollInterviewStatus, 10000);

    return () => clearInterval(interval);
  }, [currentScreen, token]);

  const fetchInterview = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/expert/interview?token=${encodeURIComponent(token)}`);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || data.error || 'Failed to load interview');
      }

      const data = await response.json();
      setInterview(data.interview);

      // Determine starting screen based on interview status
      // Self-interviews skip welcome + briefing — content manager already knows the topic
      const status = data.interview.status;
      const isSelf = data.interview.isSelfInterview || data.interview.setupMode === 'self';

      if (status === 'recorded' || status === 'processing') {
        setCurrentScreen('processing');
      } else if (status === 'completed') {
        setCurrentScreen('complete');
      } else if (status === 'recording_started' || status === 'recording_paused') {
        setCurrentScreen('recording');
      } else if (status === 'setup_completed') {
        setCurrentScreen('ready');
      } else if (status === 'setup_started') {
        setCurrentScreen('setup');
      } else if (status === 'briefing_viewed') {
        // Admin has seen briefing, go to setup
        setCurrentScreen('setup');
      } else if (status === 'briefing_ready') {
        // Admin needs to see briefing first (unless self-interview)
        setCurrentScreen(isSelf ? 'setup' : 'briefing');
      } else {
        // Self-interviews skip welcome/briefing entirely
        setCurrentScreen(isSelf ? 'setup' : 'welcome');
      }
    } catch (err) {
      console.error('Error fetching interview:', err);
      setError(err instanceof Error ? err.message : 'Failed to load interview');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status: string, metadata?: any) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/expert/interview/status?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, metadata })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleNext = async (nextScreen: InterviewScreen) => {
    // Update status based on screen transitions
    // Status values must match backend's valid statuses list
    const statusMap: Partial<Record<InterviewScreen, string>> = {
      briefing: 'briefing_viewed',
      setup: 'setup_started',
      ready: 'setup_completed',
      recording: 'recording_started',
      // Note: 'processing' status is NOT updated here because the backend
      // already sets status to 'completed' when transcript is submitted
      // Updating to 'recording_completed' would overwrite the 'completed' status
      complete: 'submitted'
    };

    const status = statusMap[nextScreen];
    if (status) {
      await updateStatus(status);
    }

    setCurrentScreen(nextScreen);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
            <h2 className="text-xl font-semibold mb-2">Loading Interview...</h2>
            <p className="text-sm text-muted-foreground">
              Please wait while we prepare your interview
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong className="block mb-2">Unable to Access Interview</strong>
                {error}
              </AlertDescription>
            </Alert>
            <div className="mt-4 text-sm text-muted-foreground">
              <p className="mb-2">Possible reasons:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>The link has expired</li>
                <li>The link is invalid</li>
                <li>The interview has been cancelled</li>
              </ul>
              <p className="mt-4">
                Please contact the interviewer for a new link.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!interview) {
    return null;
  }

  const isSelfInterview = interview.isSelfInterview || interview.setupMode === 'self';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
      {/* Self-interview banner — "You are the expert" */}
      {isSelfInterview && currentScreen !== 'complete' && currentScreen !== 'processing' && (
        <div className="sticky top-0 z-50 bg-primary text-primary-foreground py-2 px-4 shadow-md">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">You are the expert</span>
              <span className="opacity-80 hidden sm:inline">
                — self-interview on "{interview.originalPrompt || interview.approachData?.topic || 'your topic'}"
              </span>
            </div>
            <span className="text-xs opacity-80 hidden md:inline">
              The journalist will ask, you answer. No external invitation needed.
            </span>
          </div>
        </div>
      )}

      {currentScreen === 'welcome' && (
        <ExpertWelcomeScreen
          interview={interview}
          onNext={() => handleNext('briefing')}
        />
      )}

      {currentScreen === 'briefing' && (
        <ExpertBriefingScreen
          interview={interview}
          token={token!}
          onNext={() => handleNext('setup')}
          onBack={() => setCurrentScreen('welcome')}
        />
      )}

      {currentScreen === 'setup' && (
        <ExpertSetupScreen
          interview={interview}
          token={token!}
          onNext={() => handleNext('ready')}
          onBack={() => setCurrentScreen('briefing')}
          onDeviceSelected={setSelectedAudioDevice}
        />
      )}

      {currentScreen === 'ready' && (
        <ExpertReadyScreen
          interview={interview}
          onStart={() => handleNext('recording')}
          onBack={() => setCurrentScreen('setup')}
        />
      )}

      {currentScreen === 'recording' && (
        <ExpertRecordingScreen
          interview={interview}
          token={token!}
          onComplete={() => handleNext('processing')}
          selectedAudioDevice={selectedAudioDevice}
        />
      )}

      {(currentScreen === 'processing' || currentScreen === 'complete') && (
        <ExpertCompletionScreen
          interview={interview}
          token={token!}
          status={currentScreen}
        />
      )}
    </div>
  );
}
